// ota/versionGate.ts
import { App } from '@capacitor/app'
import { Preferences } from '@capacitor/preferences'

const OTA_STATE_KEY = 'ota:appliedBuild'
const OTA_ETAG_KEY = 'ota:etag'

function parseBuildFromBundleId(bundleId: string): number {
  // e.g. "1.0.0+22" -> 22
  const m = bundleId.match(/\+(\d+)$/)
  return m ? parseInt(m[1], 10) : 0
}

export async function shouldApplyBundle(bundleId: string): Promise<boolean> {
  const { build } = await App.getInfo()              // native build from APK
  const nativeBuild = Number(build) || 0

  const applied = (await Preferences.get({ key: OTA_STATE_KEY })).value
  const appliedOtaBuild = applied ? Number(applied) : 0

  const bundleBuild = parseBuildFromBundleId(bundleId)

  // Use Capacitor's logging for better Android visibility
  console.log(`[OTA] Version gate check: bundleId=${bundleId}, bundleBuild=${bundleBuild}, nativeBuild=${nativeBuild}, appliedOtaBuild=${appliedOtaBuild}, maxAllowed=${Math.max(nativeBuild, appliedOtaBuild)}`)

  const ok =
    bundleBuild > nativeBuild &&       // never downgrade below APK
    bundleBuild > appliedOtaBuild      // never rollback OTA

  if (!ok) {
    console.log(
      `[OTA] skip bundle ${bundleId} (bundleBuild=${bundleBuild}) ` +
      `<= max(native=${nativeBuild}, ota=${appliedOtaBuild})`
    )
  } else {
    console.log(`[OTA] applying ${bundleId} (bundleBuild=${bundleBuild} > max(${nativeBuild}, ${appliedOtaBuild}))`)
  }
  return ok
}

export async function rememberAppliedBundle(bundleId: string) {
  const b = parseBuildFromBundleId(bundleId)
  await Preferences.set({ key: OTA_STATE_KEY, value: String(b) })
}

/**
 * The build number the gate currently considers applied.
 *
 * This Preferences key is the authoritative gate — STORAGE_KEYS.BUNDLE_VERSION is only
 * a localStorage mirror that other paths (rollbackLast, clearOTACache) blank out. Any
 * snapshot taken for rollback has to come from here, or a restore can silently write
 * back an empty value and reopen a downgrade window.
 */
export async function getAppliedBundleBuild(): Promise<number> {
  const applied = (await Preferences.get({ key: OTA_STATE_KEY })).value
  return applied ? Number(applied) || 0 : 0
}

/**
 * Sets the gate directly from a build number, for restoring a snapshot.
 */
export async function rememberAppliedBuild(build: number) {
  await Preferences.set({ key: OTA_STATE_KEY, value: String(build) })
}

/**
 * Undoes rememberAppliedBundle.
 *
 * Needed when an activation is recorded optimistically and then fails: on a device
 * that has never taken an OTA there is no previous value to restore, so "restore the
 * old one" is not enough — the key has to be removed, or the gate stays advanced and
 * shouldApplyBundle skips that version permanently.
 */
export async function forgetAppliedBundle() {
  await Preferences.remove({ key: OTA_STATE_KEY })
}

export async function withOtaRequestHeaders(base: Record<string, string> = {}) {
  const { version, build } = await App.getInfo()
  const etag = (await Preferences.get({ key: OTA_ETAG_KEY })).value

  const headers: Record<string, string> = {
    ...base,
    Accept: 'application/json',
    'User-Agent': `DeliveryRider-OTA/1.0 (android); app=${version}+${build}`,
    'X-App-Version': String(version),
    'X-App-Build': String(build),
  }
  if (etag) headers['If-None-Match'] = etag
  return headers
}

export async function rememberEtag(etag?: string | null) {
  if (etag) await Preferences.set({ key: OTA_ETAG_KEY, value: etag })
}
